import { useMemo, useCallback } from "react";
import { UneligibleTradeReason } from "@cede/types";
import { getTradingCampaignDescription } from "@cede/utils";
import { useCampaign } from "./useCampaign";
import { useCampaignEpochSelection } from "./useCampaignEpochSelection";
import { parseEligibilityRequirements } from "@/utils/campaign/eligibility";
import { useParams } from "next/navigation";

type UseEligibilityRequirementsProps = {
  invalidAccessReason: UneligibleTradeReason;
};

export function useEligibilityRequirements({ invalidAccessReason }: UseEligibilityRequirementsProps) {
  const { id: campaignName } = useParams<{ id: string }>();

  const { campaign } = useCampaign(
    invalidAccessReason === UneligibleTradeReason.RESTRICTED_PARTICIPANTS ? campaignName : undefined,
  );

  const { selectedEpoch } = useCampaignEpochSelection({ campaign });

  const eligibilityRequirements = useMemo(() => {
    if (!campaign) return [];
    const description = getTradingCampaignDescription(campaign, selectedEpoch);
    if (!description) return [];
    return parseEligibilityRequirements(description);
  }, [campaign, selectedEpoch]);

  const handleScrollToDescription = useCallback(() => {
    const rulesSection = document.querySelector("[data-campaign-rules]");
    if (rulesSection) {
      rulesSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return { eligibilityRequirements, handleScrollToDescription };
}
